import { formService, FormTemplate } from "./formService";

export interface FormSuggestion {
	form: FormTemplate;
	relevanceScore: number;
	reason: string;
	keywords: string[];
}

export interface ChatAnalysis {
	jurisdiction?: string;
	deviceClass?: string;
	deviceType?: string;
	intent?: string;
	keywords: string[];
	mentionedForms: string[];
	regulatoryTopics: string[];
}

class FormSuggestionService {
	private formTemplates: FormTemplate[] = [];
	private initialized = false;

	async initialize() {
		if (!this.initialized) {
			try {
				// Load all forms (no jurisdiction filter) to have complete form list for suggestions
				const response = await formService.getFormTemplates(1, 1000);
				this.formTemplates = response.forms || [];
				console.log(`📋 FormSuggestionService initialized with ${this.formTemplates.length} forms`);
				this.initialized = true;
			} catch (error) {
				console.error("Failed to initialize form templates:", error);
			}
		}
	}

	/**
	 * Analyze chat messages and suggest relevant forms
	 */
	async suggestForms(
		messages: Array<{ content: string; type: string }>,
		jurisdiction?: string
	): Promise<FormSuggestion[]> {
		await this.initialize();

		if (this.formTemplates.length === 0) {
			return [];
		}

		// Combine ALL messages (both user and assistant) for analysis
		// This ensures we detect forms mentioned in the assistant's response
		const allMessages = messages
			.map((msg) => msg.content)
			.join(" ");

		if (!allMessages.trim()) {
			return [];
		}

		// Analyze the conversation (including assistant messages)
		const analysis = this.analyzeConversation(allMessages, jurisdiction);

		// Get suggestions based on analysis
		const suggestions = this.getFormSuggestions(analysis);

		// Sort by relevance score
		return suggestions.sort((a, b) => b.relevanceScore - a.relevanceScore);
	}

	/**
	 * Analyze conversation to extract key information
	 */
	private analyzeConversation(
		text: string,
		jurisdiction?: string
	): ChatAnalysis {
		const lowerText = text.toLowerCase();

		// Extract jurisdiction - prioritize passed jurisdiction, then detect from text
		let detectedJurisdiction = jurisdiction;
		if (!detectedJurisdiction) {
			// Check for EU MDR
			if (
				(lowerText.includes("eu") || lowerText.includes("europe")) &&
				(lowerText.includes("mdr") || lowerText.includes("ce marking"))
			) {
				detectedJurisdiction = "eu_mdr";
			} else if (
				lowerText.includes("us") ||
				lowerText.includes("fda") ||
				lowerText.includes("united states") ||
				lowerText.includes("usa")
			) {
				detectedJurisdiction = "us_fda";
			} else if (
				lowerText.includes("australia") ||
				lowerText.includes("tga") ||
				lowerText.includes("artg")
			) {
				detectedJurisdiction = "australia_tga";
			} else if (
				lowerText.includes("canada") ||
				lowerText.includes("health canada")
			) {
				detectedJurisdiction = "canada_hc";
			}
			// If no jurisdiction is explicitly mentioned, return null to ask for clarification
		}

		// Extract device class
		let deviceClass: string | undefined;
		if (lowerText.includes("class i") || lowerText.includes("class_i")) {
			deviceClass = "class_i";
		} else if (
			lowerText.includes("class ii") ||
			lowerText.includes("class_ii")
		) {
			deviceClass = "class_ii";
		} else if (
			lowerText.includes("class iii") ||
			lowerText.includes("class_iii")
		) {
			deviceClass = "class_iii";
		} else if (
			lowerText.includes("class iia") ||
			lowerText.includes("class_iia")
		) {
			deviceClass = "class_iia";
		} else if (
			lowerText.includes("class iib") ||
			lowerText.includes("class_iib")
		) {
			deviceClass = "class_iib";
		}

		// Extract device type
		let deviceType: string | undefined;
		if (
			lowerText.includes("cardiac") ||
			lowerText.includes("heart") ||
			lowerText.includes("ecg")
		) {
			deviceType = "cardiac";
		} else if (
			lowerText.includes("surgical") ||
			lowerText.includes("surgery")
		) {
			deviceType = "surgical";
		} else if (
			lowerText.includes("diagnostic") ||
			lowerText.includes("diagnosis")
		) {
			deviceType = "diagnostic";
		} else if (
			lowerText.includes("implant") ||
			lowerText.includes("implantable")
		) {
			deviceType = "implantable";
		} else if (
			lowerText.includes("software") ||
			lowerText.includes("ai") ||
			lowerText.includes("digital")
		) {
			deviceType = "software";
		}

		// Extract intent
		let intent: string | undefined;
		if (lowerText.includes("manufactur") || lowerText.includes("produc")) {
			intent = "manufacturing";
		} else if (
			lowerText.includes("import") ||
			lowerText.includes("distribut")
		) {
			intent = "import";
		} else if (lowerText.includes("clinical") || lowerText.includes("trial")) {
			intent = "clinical";
		} else if (
			lowerText.includes("license") ||
			lowerText.includes("approval")
		) {
			intent = "licensing";
		} else if (
			lowerText.includes("register") ||
			lowerText.includes("registration")
		) {
			intent = "registration";
		} else if (lowerText.includes("quality") || lowerText.includes("qms")) {
			intent = "quality";
		}

		// Extract keywords
		const keywords = this.extractKeywords(lowerText);

		// Extract mentioned forms
		const mentionedForms = this.extractMentionedForms(lowerText);

		// Extract regulatory topics
		const regulatoryTopics = this.extractRegulatoryTopics(lowerText);

		return {
			jurisdiction: detectedJurisdiction,
			deviceClass,
			deviceType,
			intent,
			keywords,
			mentionedForms,
			regulatoryTopics,
		};
	}

	/**
	 * Extract relevant keywords from text
	 */
	private extractKeywords(text: string): string[] {
		const keywordPatterns = [
			"manufacturing",
			"production",
			"import",
			"export",
			"distribution",
			"clinical trial",
			"clinical study",
			"clinical evaluation",
			"quality management",
			"qms",
			"iso 13485",
			"risk management",
			"risk assessment",
			"technical documentation",
			"technical file",
			"ce marking",
			"ce mark",
			"conformity assessment",
			"fda approval",
			"fda submission",
			"510k",
			"pma",
			"license",
			"registration",
			"biocompatibility",
			"sterilization",
			"packaging",
			"software",
			"cybersecurity",
			"data protection",
			"post-market surveillance",
			"vigilance",
			"adverse events",
			"labeling",
			"instructions for use",
			"ifu",
			"medical device",
			"ivd",
			"in vitro diagnostic",
		];

		return keywordPatterns.filter((keyword) => text.includes(keyword));
	}

	/**
	 * Extract mentioned form names/codes
	 * Uses regex with word boundaries to match exact form codes (prevents MD-3 from matching MD-39)
	 */
	private extractMentionedForms(text: string): string[] {
		const mentioned: string[] = [];
		
		// Pattern for FDA forms: 510(k), PMA, IDE, ANDA
		const fdaPatterns = [
			{ pattern: /\b510\s*\(?\s*k\s*\)?\b/gi, code: '510k' },
			{ pattern: /\bpma\b/gi, code: 'pma' },
			{ pattern: /\bide\b/gi, code: 'ide' },
			{ pattern: /\banda\b/gi, code: 'anda' },
		];
		
		for (const { pattern, code } of fdaPatterns) {
			if (pattern.test(text) && !mentioned.includes(code)) {
				mentioned.push(code);
			}
		}
		
		// Pattern for EU MDR forms
		const euMdrPattern = /\beu[- ]?mdr[- ]?(\d+)\b/gi;
		const euMatches = Array.from(text.matchAll(euMdrPattern));
		for (const match of euMatches) {
			const num = match[1];
			const formCode = num ? `eu-mdr-${num}` : 'eu-mdr';
			if (!mentioned.includes(formCode)) {
				mentioned.push(formCode);
			}
		}
		
		return mentioned;
	}

	/**
	 * Extract regulatory topics
	 */
	private extractRegulatoryTopics(text: string): string[] {
		const topicPatterns = [
			"medical device regulation",
			"mdr",
			"fda",
			"ce marking",
			"conformity assessment",
			"notified body",
			"clinical evaluation",
			"clinical trial",
			"clinical study",
			"quality management system",
			"qms",
			"iso 13485",
			"risk management",
			"risk assessment",
			"risk analysis",
			"biocompatibility",
			"sterilization",
			"packaging",
			"software as medical device",
			"samd",
			"cybersecurity",
			"post-market surveillance",
			"vigilance",
			"adverse events",
			"labeling",
			"instructions for use",
			"ifu",
			"in vitro diagnostic",
			"ivd",
			"ivdr",
		];

		return topicPatterns.filter((topic) => text.includes(topic));
	}

	/**
	 * Get form suggestions based on analysis
	 */
	private getFormSuggestions(analysis: ChatAnalysis): FormSuggestion[] {
		const suggestions: FormSuggestion[] = [];

		for (const form of this.formTemplates) {
			let relevanceScore = 0;
			const matchedKeywords: string[] = [];
			let reason = "";

			// Check if form is explicitly mentioned first (highest priority)
			// Use EXACT matching to avoid false positives (e.g., MD-3 matching MD-39)
			let isExplicitlyMentioned = false;
			let matchedMentionedForm: string | null = null;
			
			// Map common form mentions to keywords that should match form names/descriptions
			const formKeywordMap: Record<string, string[]> = {
				'510k': ['510(k)', '510k', 'premarket notification', '510 k'],
				'pma': ['pma', 'premarket approval', 'premarket approval application'],
				'ide': ['ide', 'investigational device exemption'],
				'anda': ['anda', 'abbreviated new drug application']
			};
			
			for (const mentionedForm of analysis.mentionedForms) {
				const formCodeLower = (form.form_code || '').toLowerCase().trim();
				const formNameLower = (form.form_name || '').toLowerCase();
				const formDescLower = (form.form_description || '').toLowerCase();
				const mentionedLower = mentionedForm.toLowerCase().trim();
				
				// Normalize: remove "form " prefix and dashes/spaces, but keep numbers separate
				// e.g., "md-3" -> "md3", "form md-3" -> "md3", "MD-39" -> "md39"
				const normalizeFormCode = (code: string): string => {
					return code
						.replace(/^form\s+/i, '')
						.replace(/^form-/i, '')
						.replace(/-/g, '')
						.replace(/\s+/g, '')
						.toLowerCase();
				};
				
				const normalizedFormCode = normalizeFormCode(formCodeLower);
				const normalizedMentioned = normalizeFormCode(mentionedLower);
				
				// EXACT match on form code
				if (normalizedFormCode === normalizedMentioned) {
					isExplicitlyMentioned = true;
					matchedMentionedForm = mentionedForm;
					break;
				}
				
				// Also check if form code contains the mentioned form as a complete word
				// e.g., "MD-3" should match "MD-3" but not "MD-39"
				// Use word boundary matching
				const formCodePattern = new RegExp(`\\b${normalizedMentioned}\\b`, 'i');
				if (formCodePattern.test(normalizedFormCode)) {
					isExplicitlyMentioned = true;
					matchedMentionedForm = mentionedForm;
					break;
				}
				
				// Check if mentioned form maps to keywords that appear in form name/description
				// This handles cases like "510k" -> forms with "510(k)" or "premarket notification" in name
				if (formKeywordMap[mentionedLower]) {
					const keywords = formKeywordMap[mentionedLower];
					const formText = `${formNameLower} ${formDescLower}`;
					if (keywords.some(keyword => formText.includes(keyword.toLowerCase()))) {
						isExplicitlyMentioned = true;
						matchedMentionedForm = mentionedForm;
						break;
					}
				}
				
				// Also check if form name/description contains the mentioned form directly
				const formText = `${formNameLower} ${formDescLower}`;
				if (formText.includes(mentionedLower) || formText.includes(normalizedMentioned)) {
					isExplicitlyMentioned = true;
					matchedMentionedForm = mentionedForm;
					break;
				}
			}

			// Jurisdiction match - must match exactly (unless form is explicitly mentioned)
			const formJurisdiction = (form.jurisdiction || '').toLowerCase();
			const analysisJurisdiction = analysis.jurisdiction ? analysis.jurisdiction.toLowerCase() : '';
			
			if (analysis.jurisdiction) {
				if (formJurisdiction === analysisJurisdiction) {
					relevanceScore += 50;
					reason += "Matches selected jurisdiction. ";
				} else if (!isExplicitlyMentioned) {
					// If jurisdiction doesn't match and form is not explicitly mentioned, skip
					continue;
				}
				// If form is explicitly mentioned, continue even if jurisdiction doesn't match
			}

			// Device class match
			if (
				analysis.deviceClass &&
				form.applicable_device_classes.includes(analysis.deviceClass)
			) {
				relevanceScore += 30;
				reason += "Matches device class. ";
			}

			// Intent match based on form category
			if (analysis.intent) {
				if (
					analysis.intent === "manufacturing" &&
					form.form_category === "manufacturing"
				) {
					relevanceScore += 25;
					reason += "Relevant for manufacturing. ";
				} else if (
					analysis.intent === "import" &&
					form.form_category === "import"
				) {
					relevanceScore += 25;
					reason += "Relevant for import. ";
				} else if (
					analysis.intent === "clinical" &&
					form.form_category === "clinical"
				) {
					relevanceScore += 25;
					reason += "Relevant for clinical trials. ";
				} else if (
					analysis.intent === "licensing" &&
					form.form_category === "licensing"
				) {
					relevanceScore += 25;
					reason += "Relevant for licensing. ";
				} else if (
					analysis.intent === "quality" &&
					form.form_category === "quality"
				) {
					relevanceScore += 25;
					reason += "Relevant for quality management. ";
				}
			}

			// Keyword matching in form name and description
			const formText =
				`${form.form_name} ${form.form_description}`.toLowerCase();
			for (const keyword of analysis.keywords) {
				if (formText.includes(keyword)) {
					relevanceScore += 10;
					matchedKeywords.push(keyword);
				}
			}

			// Mentioned forms match (already checked above, but add score boost)
			if (isExplicitlyMentioned && matchedMentionedForm) {
				relevanceScore += 100; // Very high score for explicitly mentioned forms
				reason += `Specifically mentioned in conversation (${matchedMentionedForm}). `;
			}

			// Regulatory topics match
			for (const topic of analysis.regulatoryTopics) {
				if (formText.includes(topic)) {
					relevanceScore += 15;
					matchedKeywords.push(topic);
				}
			}

			// Device type matching
			if (analysis.deviceType) {
				if (analysis.deviceType === "cardiac" && formText.includes("cardiac")) {
					relevanceScore += 20;
					reason += "Relevant for cardiac devices. ";
				} else if (
					analysis.deviceType === "surgical" &&
					formText.includes("surgical")
				) {
					relevanceScore += 20;
					reason += "Relevant for surgical devices. ";
				} else if (
					analysis.deviceType === "diagnostic" &&
					formText.includes("diagnostic")
				) {
					relevanceScore += 20;
					reason += "Relevant for diagnostic devices. ";
				} else if (
					analysis.deviceType === "implantable" &&
					formText.includes("implant")
				) {
					relevanceScore += 20;
					reason += "Relevant for implantable devices. ";
				} else if (
					analysis.deviceType === "software" &&
					formText.includes("software")
				) {
					relevanceScore += 20;
					reason += "Relevant for software devices. ";
				}
			}

			// Only include forms with meaningful relevance
			if (relevanceScore > 10) {
				suggestions.push({
					form,
					relevanceScore,
					reason: reason || "Relevant based on conversation analysis.",
					keywords: matchedKeywords,
				});
			}
		}

		return suggestions;
	}

	/**
	 * Get top N form suggestions
	 */
	async getTopFormSuggestions(
		messages: Array<{ content: string; type: string }>,
		jurisdiction?: string,
		limit: number = 3,
		detectedForms?: Array<{
			form_id?: string;
			form_code?: string;
			form_name?: string;
			form_download_url?: string;
			checklist_download_url?: string;
			has_form_file?: boolean;
			has_checklist?: boolean;
			[key: string]: any;
		}>
	): Promise<FormSuggestion[]> {
		await this.initialize();
		
		// PRIORITY 1: Use forms detected from the answer (these are what the AI actually mentioned)
		if (detectedForms && detectedForms.length > 0) {
			const detectedSuggestions: FormSuggestion[] = [];
			
			for (const detectedForm of detectedForms) {
				// Find matching form in templates
				const formCode = detectedForm.form_code || detectedForm.form_name || '';
				const detectedCodeLower = formCode.toLowerCase().trim();
				
				// Normalize form codes for matching (remove special chars, spaces, etc.)
				const normalizeFormCode = (code: string): string => {
					return code
						.toLowerCase()
						.replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric
						.trim();
				};
				
				const normalizedDetected = normalizeFormCode(detectedCodeLower);
				
				const matchingForm = this.formTemplates.find(form => {
					const templateCode = (form.form_code || '').toLowerCase().trim();
					const templateName = (form.form_name || '').toLowerCase();
					const templateDesc = (form.form_description || '').toLowerCase();
					
					// Try exact match on form_code
					if (templateCode === detectedCodeLower) {
						return true;
					}
					
					// Try normalized match (510(k) vs 510k, PMA vs pma, etc.)
					const normalizedTemplate = normalizeFormCode(templateCode);
					if (normalizedTemplate === normalizedDetected && normalizedTemplate.length > 0) {
						return true;
					}
					
					// Try matching in form_name (check if form name contains the detected code or vice versa)
					if (templateName.includes(detectedCodeLower) || detectedCodeLower.includes(templateName)) {
						return true;
					}
					
					// Try matching in form_description (for cases like "510(k)" in description)
					if (templateDesc.includes(detectedCodeLower) || detectedCodeLower.includes(templateDesc)) {
						return true;
					}
					
					// Special handling for common FDA forms
					if (normalizedDetected === '510k' || normalizedDetected === '510') {
						if (normalizeFormCode(templateCode) === '510k' || 
						    templateName.includes('510') || 
						    templateName.includes('premarket notification')) {
							return true;
						}
					}
					
					if (normalizedDetected === 'pma') {
						if (normalizeFormCode(templateCode) === 'pma' || 
						    templateName.includes('premarket approval')) {
							return true;
						}
					}
					
					return false;
				});
				
				if (matchingForm) {
					detectedSuggestions.push({
						form: matchingForm,
						relevanceScore: 100, // Highest priority for detected forms
						keywords: [formCode],
						reason: `Mentioned in answer: ${formCode}`
					});
				} else {
					// If form not found in templates, create a suggestion from detected form data
					detectedSuggestions.push({
						form: {
							id: detectedForm.form_id || '',
							form_code: detectedForm.form_code || '',
							form_name: detectedForm.form_name || formCode,
							form_description: `Form mentioned in answer: ${formCode}`,
							jurisdiction: jurisdiction || 'unknown',
							form_type: 'application',
							form_category: 'regulatory',
							applicable_device_classes: [],
							required_fields: [],
							optional_fields: [],
							supporting_documents: [],
							file_format: 'pdf',
							s3_url: detectedForm.form_download_url,
							original_url: detectedForm.form_download_url,
							source_page_url: detectedForm.form_download_url,
							version: '1.0',
							is_current: true
						},
						relevanceScore: 100,
						keywords: [formCode],
						reason: `Mentioned in answer: ${formCode}`
					});
				}
			}
			
			// If we have detected forms, prioritize them and add other suggestions if needed
			if (detectedSuggestions.length > 0) {
				const otherSuggestions = await this.suggestForms(messages, jurisdiction);
				// Filter out duplicates (forms already in detectedSuggestions)
				const detectedCodes = new Set(
					detectedSuggestions.map(s => (s.form.form_code || '').toLowerCase().trim())
				);
				const uniqueOtherSuggestions = otherSuggestions.filter(
					s => !detectedCodes.has((s.form.form_code || '').toLowerCase().trim())
				);
				
				// Combine: detected forms first, then other suggestions
				const combined = [...detectedSuggestions, ...uniqueOtherSuggestions];
				return combined.slice(0, limit);
			}
		}
		
		// FALLBACK: Use regular suggestion logic if no detected forms
		const suggestions = await this.suggestForms(messages, jurisdiction);
		return suggestions.slice(0, limit);
	}
}

export const formSuggestionService = new FormSuggestionService();
